<!-- 자재 마스터 관리 (2026-07-15, Phase 1) —
     자재코드 기준정보. 시드는 seed_material_master 커맨드, 여기선 조회·수정·추가.
     검토서: docs/purchasing-folder-review-2026-07.md -->
<template>
  <div class="mm-view">
    <div class="page-header">
      <h2>자재 마스터</h2>
      <el-button size="small" @click="helpVisible = true">도움말</el-button>
      <HelpPanel v-model="helpVisible" :content-path="'screens/material-master'" />
      <div class="header-actions">
        <el-button type="primary" @click="openAdd">자재 추가</el-button>
        <el-button @click="downloadExcel" :loading="excelDownloading">엑셀 다운로드</el-button>
        <el-button @click="openExcelDialog">엑셀 업로드</el-button>
      </div>
    </div>

    <el-tabs v-model="activeTab">
      <el-tab-pane label="마스터 목록" name="list">

    <el-row :gutter="10" class="mb-2" align="middle">
      <el-col :span="6">
        <el-input
          v-model="query"
          placeholder="검색 (자재코드, 품명, 별칭)"
          clearable
          @input="fetchList"
        />
      </el-col>
      <el-col :span="4">
        <el-select v-model="typeFilter" placeholder="자재유형" clearable @change="fetchList">
          <el-option label="원자재" value="RAW" />
          <el-option label="부자재" value="ACC" />
        </el-select>
      </el-col>
      <el-col :span="4">
        <!-- 품목군 (2026-07-15, 마감 분석) — 마감 집계 축 -->
        <el-select v-model="categoryFilter" placeholder="품목군" clearable filterable @change="fetchList">
          <el-option v-for="c in categories" :key="c" :label="c" :value="c" />
        </el-select>
      </el-col>
      <el-col :span="4">
        <el-checkbox v-model="activeOnly" @change="fetchList">사용중만</el-checkbox>
      </el-col>
      <el-col :span="6" style="text-align:right">
        <span class="hint">{{ rows.length }}건 — 코드(RM-/AC-)는 자동 채번</span>
      </el-col>
    </el-row>

    <el-table :data="rows" v-loading="loading" size="small" border stripe>
      <el-table-column prop="code" label="자재코드" width="100" />
      <el-table-column prop="material_type_display" label="유형" width="80">
        <template #default="{ row }">
          <el-tag :type="row.material_type === 'RAW' ? 'primary' : 'success'" size="small">
            {{ row.material_type_display }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="category" label="품목군" width="90" />
      <el-table-column prop="name" label="표준 품명" min-width="160" />
      <el-table-column prop="specification" label="규격" width="120" />
      <el-table-column prop="unit" label="단위" width="70" />
      <el-table-column prop="default_supplier_name" label="기본 공급사" width="130" />
      <el-table-column label="별칭" min-width="180">
        <template #default="{ row }">
          <el-tag
            v-for="(a, i) in (row.aliases || []).slice(0, 3)"
            :key="i" size="small" type="info" class="alias-tag"
          >{{ a }}</el-tag>
          <span v-if="(row.aliases || []).length > 3" class="hint">
            +{{ row.aliases.length - 3 }}
          </span>
        </template>
      </el-table-column>
      <el-table-column label="사용" width="70" align="center">
        <template #default="{ row }">
          <el-tag :type="row.is_active ? 'success' : 'info'" size="small">
            {{ row.is_active ? '사용' : '중지' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="작업" width="70" align="center">
        <template #default="{ row }">
          <el-button link type="primary" size="small" @click="openEdit(row)">수정</el-button>
        </template>
      </el-table-column>
    </el-table>

      </el-tab-pane>

      <!-- 등록 후보 (2026-07-15) — 마스터 미연결 발주 품명 자동 수집.
           발주는 막지 않고, 여기서 검수(승격/별칭 병합)를 거쳐 마스터가
           쓰면서 자란다. 무통제 자동 등록의 오염 방지 관문. -->
      <el-tab-pane :label="`등록 후보 (${candidates.length})`" name="candidates">
        <div class="hint mb-2">
          마스터에 없는 품명으로 발주된 항목입니다.
          신규 자재는 [승격], 기존 자재의 표기 변형은 [별칭 병합]으로 처리하면
          과거 발주 이력까지 소급 연결됩니다.
        </div>
        <el-table :data="candidates" v-loading="candLoading" size="small" border stripe>
          <el-table-column prop="name" label="품명" min-width="180" />
          <el-table-column prop="specification" label="규격" width="120" />
          <el-table-column label="유형" width="80">
            <template #default="{ row }">
              <el-tag :type="row.material_type === 'RAW' ? 'primary' : 'success'" size="small">
                {{ row.material_type === 'RAW' ? '원자재' : '부자재' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="order_count" label="발주횟수" width="80" align="right" />
          <el-table-column prop="last_order_date" label="최근발주일" width="110" />
          <el-table-column label="최근단가" width="100" align="right">
            <template #default="{ row }">
              {{ row.last_unit_price ? Number(row.last_unit_price).toLocaleString() : '' }}
            </template>
          </el-table-column>
          <el-table-column label="처리" width="170" align="center">
            <template #default="{ row }">
              <el-button link type="primary" size="small" @click="openPromote(row)">승격</el-button>
              <el-button link type="warning" size="small" @click="openMerge(row)">별칭 병합</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <!-- 단가표 (2026-07-17, Phase 3) — 공급사 단가표 버전 적재 -->
      <el-tab-pane label="단가표" name="prices">
        <el-row :gutter="10" class="mb-2" align="middle">
          <el-col :span="6">
            <el-select v-model="priceSupplierFilter" filterable clearable placeholder="공급사"
                       style="width:100%" @change="fetchPriceLists">
              <el-option v-for="s in suppliers" :key="s.supplier_id" :label="s.name" :value="s.supplier_id" />
            </el-select>
          </el-col>
          <el-col :span="4">
            <el-input v-model="priceSeasonFilter" placeholder="시즌 (예: 26FW)" clearable
                      @change="fetchPriceLists" />
          </el-col>
          <el-col :span="14" style="text-align:right">
            <el-button size="small" @click="downloadPriceTemplate" :loading="priceTplLoading">서식 다운로드</el-button>
            <el-button size="small" type="primary" @click="openPriceDialog">단가표 업로드</el-button>
          </el-col>
        </el-row>
        <div class="hint mb-2">
          갱신은 수정이 아니라 <b>새 적용일로 재업로드</b>합니다 (이력 보존).
          견적·발주 입력의 단가 참조는 공급사별 최신 장만 사용합니다.
        </div>
        <el-table :data="priceLists" v-loading="priceLoading" size="small" border stripe
                  highlight-current-row @current-change="selectPriceList">
          <el-table-column prop="supplier_name" label="공급사" min-width="140" />
          <el-table-column prop="season" label="시즌" width="80" />
          <el-table-column prop="effective_date" label="적용일" width="110" />
          <el-table-column prop="currency" label="통화" width="70" align="center" />
          <el-table-column prop="item_count" label="행수" width="70" align="right" />
          <el-table-column prop="source_file" label="원본 파일" min-width="180" />
          <el-table-column prop="created_by_name" label="업로드" width="90" />
          <el-table-column label="" width="70" align="center">
            <template #default="{ row }">
              <el-button link type="danger" size="small" @click="deletePriceList(row)">삭제</el-button>
            </template>
          </el-table-column>
        </el-table>
        <template v-if="selectedPriceList">
          <div class="sec-title" style="margin-top:14px">
            {{ selectedPriceList.supplier_name }} {{ selectedPriceList.season }}
            ({{ selectedPriceList.effective_date }}) — {{ priceItems.length }}행
            <el-input v-model="priceItemQuery" size="small" placeholder="품번/품명 검색" clearable
                      style="width:220px; margin-left:auto" @input="fetchPriceItems" />
          </div>
          <el-table :data="priceItems" v-loading="priceItemLoading" size="small" border stripe max-height="420">
            <el-table-column prop="item_no" label="품번" min-width="130" />
            <el-table-column prop="name" label="품명/특징" min-width="150" />
            <el-table-column label="단가" width="110" align="right">
              <template #default="{ row }">{{ Number(row.unit_price).toLocaleString() }}</template>
            </el-table-column>
            <el-table-column prop="unit" label="단위" width="60" />
            <el-table-column prop="min_condition" label="최소조건" width="130" />
            <el-table-column prop="width" label="폭" width="70" />
            <el-table-column prop="composition" label="혼용률" width="100" />
            <el-table-column label="마스터" width="110">
              <template #default="{ row }">
                <el-tag v-if="row.material_code" size="small">{{ row.material_code }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="brands_note" label="진행브랜드" min-width="120" />
          </el-table>
        </template>
      </el-tab-pane>
    </el-tabs>

    <!-- 별칭 병합 다이얼로그 -->
    <el-dialog v-model="mergeVisible" title="별칭으로 병합" width="480px">
      <div class="mb-2">
        후보 <b>{{ mergeCandidate?.name }}</b>
        <span v-if="mergeCandidate?.specification" class="hint"> · {{ mergeCandidate.specification }}</span>
        을(를) 아래 마스터의 별칭(표기 변형)으로 추가합니다.
      </div>
      <el-select
        v-model="mergeMasterId" filterable remote clearable
        :remote-method="searchMergeTarget" :loading="mergeSearching"
        placeholder="대상 마스터 검색 (코드/품명/별칭)" style="width: 100%"
      >
        <el-option
          v-for="m in mergeOptions" :key="m.id"
          :label="`[${m.code}] ${m.name}${m.specification ? ' · ' + m.specification : ''}`"
          :value="m.id"
        />
      </el-select>
      <template #footer>
        <el-button @click="mergeVisible = false">취소</el-button>
        <el-button type="primary" :disabled="!mergeMasterId" :loading="merging" @click="doMerge">병합</el-button>
      </template>
    </el-dialog>

    <!-- 추가/수정 다이얼로그 -->
    <el-dialog
      v-model="dialogVisible"
      :title="editingId ? `자재 수정 — ${form.code}` : (promoteMode ? '후보 승격 — 마스터 등록' : '자재 추가')"
      width="560px"
    >
      <el-form :model="form" label-width="90px">
        <el-form-item label="자재유형">
          <el-radio-group v-model="form.material_type" :disabled="!!editingId">
            <el-radio-button label="RAW">원자재</el-radio-button>
            <el-radio-button label="ACC">부자재</el-radio-button>
          </el-radio-group>
          <span v-if="editingId" class="hint" style="margin-left:8px">
            유형은 코드 체계와 묶여 있어 변경 불가
          </span>
        </el-form-item>
        <el-form-item label="표준 품명" required>
          <el-input v-model="form.name" />
        </el-form-item>
        <el-form-item label="품목군">
          <el-select
            v-model="form.category" filterable allow-create clearable
            default-first-option placeholder="마감 집계 축 (예: 지퍼)"
            style="width: 220px"
          >
            <el-option v-for="c in categories" :key="c" :label="c" :value="c" />
          </el-select>
        </el-form-item>
        <el-form-item label="규격">
          <el-input v-model="form.specification" />
        </el-form-item>
        <el-form-item label="단위">
          <el-input v-model="form.unit" style="width: 120px" />
        </el-form-item>
        <el-form-item label="기본 공급사">
          <!-- ⚠ Supplier PK 는 supplier_id -->
          <el-select v-model="form.default_supplier" filterable clearable placeholder="선택" style="width:100%">
            <el-option v-for="s in suppliers" :key="s.supplier_id" :label="s.name" :value="s.supplier_id" />
          </el-select>
        </el-form-item>
        <el-form-item label="별칭">
          <el-select
            v-model="form.aliases"
            multiple filterable allow-create default-first-option
            placeholder="과거 표기 변형 입력 후 Enter"
            style="width:100%"
          />
        </el-form-item>
        <el-form-item label="사용" v-if="editingId">
          <el-switch v-model="form.is_active" active-text="사용" inactive-text="중지" />
        </el-form-item>
        <el-form-item label="비고">
          <el-input v-model="form.remarks" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">취소</el-button>
        <el-button type="primary" :loading="saving" @click="save">저장</el-button>
      </template>
    </el-dialog>

    <!-- 엑셀 업로드 다이얼로그 (검증 → 반영 2단계, 2026-07-17) -->
    <el-dialog v-model="excelDialogVisible" title="자재 마스터 엑셀 업로드" width="620px"
               :close-on-click-modal="false" destroy-on-close>
      <div class="hint" style="margin-bottom:6px">
        [엑셀 다운로드] 서식 그대로 수정/행 추가 후 업로드하세요.
        자재코드가 있으면 갱신, 없으면 품명+규격으로 찾고, 못 찾으면 신규(자동채번)입니다.
        빈 칸은 변경되지 않습니다. 별칭은 [별칭추가] 열로 추가만 가능합니다(병합·삭제는 화면에서).
      </div>
      <input type="file" accept=".xlsx" @change="onExcelFileChange" style="margin: 10px 0;" />

      <template v-if="excelReport">
        <el-alert :type="excelReportHasError ? 'warning' : 'success'" :closable="false" show-icon
                  :title="excelReport.dry_run ? '검증 결과 (아직 반영 안 됨)' : '반영 완료'">
          <div>신규 {{ excelReport.materials.created.length }} ·
            수정 {{ excelReport.materials.updated.length }} ·
            오류 {{ excelReport.materials.errors.length }}</div>
        </el-alert>
        <div v-if="excelReport.materials.created.length" class="hint" style="margin-top:6px; word-break:break-all">
          신규: {{ excelReport.materials.created.join(', ') }}</div>
        <div v-if="excelReport.materials.updated.length" class="hint" style="margin-top:4px; word-break:break-all">
          수정: {{ excelReport.materials.updated.join(', ') }}</div>
        <div v-for="(err, i) in excelReport.materials.errors" :key="i"
             style="font-size:12px; color:#e6a23c; margin-top:4px">⚠ {{ err }}</div>
      </template>

      <template #footer>
        <el-button @click="excelDialogVisible = false">닫기</el-button>
        <el-button type="primary" plain :disabled="!excelFile" :loading="excelUploading"
                   @click="uploadExcel(false)">검증</el-button>
        <el-button type="primary"
                   :disabled="!excelFile || !excelReport || !excelReport.dry_run || excelReportHasError"
                   :loading="excelUploading" @click="uploadExcel(true)">반영</el-button>
      </template>
    </el-dialog>

    <!-- 단가표 업로드 다이얼로그 (2026-07-17) -->
    <el-dialog v-model="priceDialogVisible" title="단가표 업로드" width="640px"
               :close-on-click-modal="false" destroy-on-close>
      <el-form label-width="80px" size="small" inline>
        <el-form-item label="공급사" required>
          <el-select v-model="priceForm.supplier" filterable placeholder="선택" style="width:200px">
            <el-option v-for="s in suppliers" :key="s.supplier_id" :label="s.name" :value="s.supplier_id" />
          </el-select>
        </el-form-item>
        <el-form-item label="시즌">
          <el-input v-model="priceForm.season" placeholder="26FW" style="width:90px" />
        </el-form-item>
        <el-form-item label="적용일" required>
          <el-date-picker v-model="priceForm.effective_date" type="date" value-format="YYYY-MM-DD" style="width:140px" />
        </el-form-item>
        <el-form-item label="통화">
          <el-select v-model="priceForm.currency" style="width:90px">
            <el-option label="KRW" value="KRW" />
            <el-option label="USD" value="USD" />
            <el-option label="CNY" value="CNY" />
          </el-select>
        </el-form-item>
      </el-form>
      <div class="hint">
        엑셀에는 행(품번/단가...)만 담습니다 — [서식 다운로드]의 표준 서식 사용.
        같은 공급사+시즌+적용일 장은 중복 등록되지 않습니다.
        품번이 자재 마스터의 품명·별칭과 일치하면 자동 연결됩니다.
      </div>
      <input type="file" accept=".xlsx" @change="onPriceFileChange" style="margin: 10px 0;" />
      <template v-if="priceReport">
        <el-alert :type="priceReportHasError ? 'warning' : 'success'" :closable="false" show-icon
                  :title="priceReport.dry_run ? '검증 결과 (아직 반영 안 됨)' : '반영 완료'">
          <div>{{ priceReport.price.created.join(' / ') }} · 오류 {{ priceReport.price.errors.length }}</div>
        </el-alert>
        <div v-for="(err, i) in priceReport.price.errors" :key="i"
             style="font-size:12px; color:#e6a23c; margin-top:4px">⚠ {{ err }}</div>
      </template>
      <template #footer>
        <el-button @click="priceDialogVisible = false">닫기</el-button>
        <el-button type="primary" plain :disabled="!priceFile || !priceForm.supplier || !priceForm.effective_date"
                   :loading="priceUploading" @click="uploadPrice(false)">검증</el-button>
        <el-button type="primary"
                   :disabled="!priceFile || !priceReport || !priceReport.dry_run || priceReportHasError"
                   :loading="priceUploading" @click="uploadPrice(true)">반영</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import HelpPanel from '@/components/help/HelpPanel.vue'
import { ref as _helpRef } from 'vue'
const helpVisible = _helpRef(false)
import { ref, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axiosInstance from '@/api/axios'

const rows = ref([])
const loading = ref(false)
const activeTab = ref('list')
import { watch } from 'vue'
watch(activeTab, (t) => { if (t === 'prices' && !priceLists.value.length) fetchPriceLists() })

// ── 등록 후보 (2026-07-15) ──
const candidates = ref([])
const candLoading = ref(false)
const fetchCandidates = async () => {
  candLoading.value = true
  try {
    const res = await axiosInstance.get('/material-masters/candidates/')
    candidates.value = res.data || []
  } catch (err) {
    console.error('등록 후보 조회 실패:', err)
  } finally {
    candLoading.value = false
  }
}

// 승격 — 추가 다이얼로그 재사용 (저장 시 promote-candidate 호출 → 소급 연결)
const promoteMode = ref(false)
const promoteSource = ref(null)
const openPromote = (cand) => {
  editingId.value = null
  promoteMode.value = true
  promoteSource.value = cand
  form.value = {
    ...emptyForm(),
    material_type: cand.material_type || 'ACC',
    name: cand.name,
    specification: cand.specification || '',
  }
  dialogVisible.value = true
  if (!suppliers.value.length) fetchSuppliers()
}

// 별칭 병합
const mergeVisible = ref(false)
const mergeCandidate = ref(null)
const mergeMasterId = ref(null)
const mergeOptions = ref([])
const mergeSearching = ref(false)
const merging = ref(false)
const openMerge = (cand) => {
  mergeCandidate.value = cand
  mergeMasterId.value = null
  mergeOptions.value = []
  mergeVisible.value = true
}
const searchMergeTarget = async (q) => {
  if (!q) { mergeOptions.value = []; return }
  mergeSearching.value = true
  try {
    const res = await axiosInstance.get('/material-masters/search/', { params: { q } })
    mergeOptions.value = res.data || []
  } catch (err) {
    console.error('마스터 검색 실패:', err)
  } finally {
    mergeSearching.value = false
  }
}
const doMerge = async () => {
  merging.value = true
  try {
    const res = await axiosInstance.post(
      `/material-masters/${mergeMasterId.value}/merge-alias/`, {
        alias: mergeCandidate.value.name,
        specification: mergeCandidate.value.specification || '',
      })
    const l = res.data.linked || {}
    ElMessage.success(
      `별칭으로 병합했습니다. (발주항목 ${l.order_items || 0}건 · 필요자재 ${l.required_materials || 0}건 소급 연결)`)
    mergeVisible.value = false
    await Promise.all([fetchCandidates(), fetchList()])
  } catch (err) {
    const t = err.response?.data?.detail
    ElMessage.error(typeof t === 'string' ? t : '병합에 실패했습니다.')
  } finally {
    merging.value = false
  }
}
const query = ref('')
const typeFilter = ref('')
const categoryFilter = ref('')
const activeOnly = ref(true)

// 품목군 목록 — 표준 20군 + 실사용 값 (백엔드 합집합)
const categories = ref([])
const fetchCategories = async () => {
  try {
    const res = await axiosInstance.get('/material-masters/categories/')
    categories.value = res.data || []
  } catch (err) {
    console.error('품목군 목록 조회 실패:', err)
  }
}

const fetchList = async () => {
  loading.value = true
  try {
    const params = {}
    if (query.value) params.q = query.value
    if (typeFilter.value) params.material_type = typeFilter.value
    if (categoryFilter.value) params.category = categoryFilter.value
    if (activeOnly.value) params.is_active = 1
    const res = await axiosInstance.get('/material-masters/', { params })
    rows.value = res.data.results || res.data || []
  } catch (err) {
    console.error('자재 마스터 조회 실패:', err)
  } finally {
    loading.value = false
  }
}

const suppliers = ref([])
const fetchSuppliers = async () => {
  try {
    const res = await axiosInstance.get('/suppliers/')
    suppliers.value = Array.isArray(res.data.results) ? res.data.results : (res.data || [])
  } catch (err) {
    console.error('공급사 조회 실패:', err)
  }
}

const dialogVisible = ref(false)
const editingId = ref(null)
const saving = ref(false)
const form = ref({})

const emptyForm = () => ({
  material_type: 'ACC', name: '', category: '', specification: '', unit: '',
  default_supplier: null, aliases: [], is_active: true, remarks: '',
})

const openAdd = () => {
  editingId.value = null
  promoteMode.value = false
  form.value = emptyForm()
  dialogVisible.value = true
  if (!suppliers.value.length) fetchSuppliers()
}

const openEdit = (row) => {
  editingId.value = row.id
  promoteMode.value = false
  form.value = {
    code: row.code,
    material_type: row.material_type,
    name: row.name,
    category: row.category || '',
    specification: row.specification || '',
    unit: row.unit || '',
    default_supplier: row.default_supplier || null,
    aliases: [...(row.aliases || [])],
    is_active: row.is_active,
    remarks: row.remarks || '',
  }
  dialogVisible.value = true
  if (!suppliers.value.length) fetchSuppliers()
}

const save = async () => {
  if (!form.value.name) {
    ElMessage.warning('표준 품명을 입력하세요.')
    return
  }
  const payload = { ...form.value }
  if (!payload.unit) payload.unit = payload.material_type === 'RAW' ? 'YD' : 'EA'
  delete payload.code
  saving.value = true
  try {
    if (editingId.value) {
      await axiosInstance.patch(`/material-masters/${editingId.value}/`, payload)
      ElMessage.success('수정했습니다.')
    } else if (promoteMode.value) {
      const res = await axiosInstance.post('/material-masters/promote-candidate/', payload)
      const l = res.data.linked || {}
      ElMessage.success(
        `마스터로 승격했습니다. [${res.data.code}] (발주항목 ${l.order_items || 0}건 · 필요자재 ${l.required_materials || 0}건 소급 연결)`)
      promoteMode.value = false
      fetchCandidates()
    } else {
      await axiosInstance.post('/material-masters/', payload)
      ElMessage.success('추가했습니다. (자재코드 자동 부여)')
    }
    dialogVisible.value = false
    await fetchList()
  } catch (err) {
    const data = err.response?.data
    const first = data && (data.detail ?? Object.values(data)[0])
    const text = Array.isArray(first) ? first[0] : first
    ElMessage.error(typeof text === 'string' ? text : '저장에 실패했습니다.')
  } finally {
    saving.value = false
  }
}

// ── 단가표 (2026-07-17, Phase 3) ──
const priceLists = ref([])
const priceLoading = ref(false)
const priceSupplierFilter = ref(null)
const priceSeasonFilter = ref('')
const selectedPriceList = ref(null)
const priceItems = ref([])
const priceItemLoading = ref(false)
const priceItemQuery = ref('')
const priceTplLoading = ref(false)
const priceDialogVisible = ref(false)
const priceUploading = ref(false)
const priceFile = ref(null)
const priceReport = ref(null)
const priceForm = ref({ supplier: null, season: '', effective_date: '', currency: 'KRW' })
const priceReportHasError = computed(() =>
  !!priceReport.value && priceReport.value.price.errors.length > 0)

const fetchPriceLists = async () => {
  priceLoading.value = true
  try {
    const params = {}
    if (priceSupplierFilter.value) params.supplier = priceSupplierFilter.value
    if (priceSeasonFilter.value) params.season = priceSeasonFilter.value
    const res = await axiosInstance.get('/material-price-lists/', { params })
    priceLists.value = res.data.results || res.data || []
    selectedPriceList.value = null
    priceItems.value = []
    if (!suppliers.value.length) fetchSuppliers()
  } catch (e) {
    console.error('단가표 조회 실패:', e)
  } finally {
    priceLoading.value = false
  }
}
const selectPriceList = (row) => {
  selectedPriceList.value = row
  priceItemQuery.value = ''
  if (row) fetchPriceItems()
}
const fetchPriceItems = async () => {
  if (!selectedPriceList.value) return
  priceItemLoading.value = true
  try {
    const res = await axiosInstance.get(`/material-price-lists/${selectedPriceList.value.id}/items/`,
      { params: priceItemQuery.value ? { q: priceItemQuery.value } : {} })
    priceItems.value = res.data || []
  } catch (e) {
    console.error('단가 행 조회 실패:', e)
  } finally {
    priceItemLoading.value = false
  }
}
const deletePriceList = async (row) => {
  try {
    await ElMessageBox.confirm(
      `${row.supplier_name} ${row.season} (${row.effective_date}) 단가표를 삭제할까요?`, '단가표 삭제',
      { type: 'warning', confirmButtonText: '삭제', cancelButtonText: '취소' })
  } catch { return }
  try {
    await axiosInstance.delete(`/material-price-lists/${row.id}/`)
    ElMessage.success('삭제되었습니다')
    fetchPriceLists()
  } catch (e) {
    ElMessage.error('삭제에 실패했습니다')
  }
}
const downloadPriceTemplate = async () => {
  priceTplLoading.value = true
  try {
    const params = selectedPriceList.value ? { price_list: selectedPriceList.value.id } : {}
    const res = await axiosInstance.get('/material-price-lists/excel-template/',
      { params, responseType: 'blob' })
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = '단가표_서식.xlsx'
    a.click()
    URL.revokeObjectURL(url)
  } catch (e) {
    ElMessage.error('서식 다운로드에 실패했습니다')
  } finally {
    priceTplLoading.value = false
  }
}
const openPriceDialog = () => {
  priceFile.value = null
  priceReport.value = null
  priceForm.value = { supplier: priceSupplierFilter.value, season: priceSeasonFilter.value, effective_date: '', currency: 'KRW' }
  if (!suppliers.value.length) fetchSuppliers()
  priceDialogVisible.value = true
}
const onPriceFileChange = (e) => {
  priceFile.value = e.target.files?.[0] || null
  priceReport.value = null
}
const uploadPrice = async (apply) => {
  if (!priceFile.value) return
  priceUploading.value = true
  try {
    const fd = new FormData()
    fd.append('file', priceFile.value)
    fd.append('supplier', priceForm.value.supplier)
    fd.append('season', priceForm.value.season || '')
    fd.append('effective_date', priceForm.value.effective_date)
    fd.append('currency', priceForm.value.currency)
    const res = await axiosInstance.post(
      `/material-price-lists/excel-import/${apply ? '?apply=1' : ''}`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } })
    priceReport.value = res.data
    if (apply && !priceReportHasError.value) {
      ElMessage.success('단가표가 등록되었습니다')
      priceDialogVisible.value = false
      fetchPriceLists()
    }
  } catch (e) {
    console.error('단가표 업로드 실패:', e)
    ElMessage.error(e.response?.data?.detail || '단가표 업로드에 실패했습니다')
  } finally {
    priceUploading.value = false
  }
}

// ── 엑셀 다운로드/업로드 (2026-07-17) ──
const excelDownloading = ref(false)
const excelDialogVisible = ref(false)
const excelUploading = ref(false)
const excelFile = ref(null)
const excelReport = ref(null)
const excelReportHasError = computed(() =>
  !!excelReport.value && excelReport.value.materials.errors.length > 0)

const downloadExcel = async () => {
  excelDownloading.value = true
  try {
    const res = await axiosInstance.get('/material-masters/excel-export/', { responseType: 'blob' })
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = `자재마스터_${new Date().toISOString().slice(0, 10)}.xlsx`
    a.click()
    URL.revokeObjectURL(url)
  } catch (e) {
    console.error('엑셀 다운로드 실패:', e)
    ElMessage.error('엑셀 다운로드에 실패했습니다')
  } finally {
    excelDownloading.value = false
  }
}

const openExcelDialog = () => {
  excelFile.value = null
  excelReport.value = null
  excelDialogVisible.value = true
}
const onExcelFileChange = (e) => {
  excelFile.value = e.target.files?.[0] || null
  excelReport.value = null
}
const uploadExcel = async (apply) => {
  if (!excelFile.value) return
  excelUploading.value = true
  try {
    const fd = new FormData()
    fd.append('file', excelFile.value)
    const res = await axiosInstance.post(
      `/material-masters/excel-import/${apply ? '?apply=1' : ''}`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    excelReport.value = res.data
    if (apply) {
      ElMessage.success('반영되었습니다')
      fetchList()
      fetchCandidates()
    }
  } catch (e) {
    console.error('엑셀 업로드 실패:', e)
    ElMessage.error(e.response?.data?.detail || '엑셀 업로드에 실패했습니다')
  } finally {
    excelUploading.value = false
  }
}

onMounted(() => { fetchList(); fetchCategories(); fetchCandidates() })
</script>

<style scoped>
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.mb-2 { margin-bottom: 8px; }
.hint { font-size: 12px; color: #909399; }
.alias-tag { margin-right: 4px; }
</style>
